import React, { useState, useEffect } from 'react'
import { compose } from 'redux'
import { withRouter } from 'withRouter'
import { FormattedMessage } from 'react-intl'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { useParams } from 'react-router-dom'

import messages from 'utils/messages'
import ProcessPage from 'components/ProcessPage'
import Button from 'components/Button'
import className from 'classnames'
import Modal from 'components/CardModal'
import Switch from 'components/Input/Switch'
import AugmentationConfirmation from './AugmentationConfirmation'
import LoadingIndicator from 'components/LoadingIndicator'
import slugify from 'react-slugify'
import AugmentationEditor from './AugmentationEditor'
import { getPermission } from 'utils/permissions'
import { defaultNotifier } from 'functions/notificationHandler'
import { makeSelectFetchWorkspace } from 'containers/Settings/Workspace/selectors'
import { LIBRARY_ACCENT } from 'utils/constants'
import { augmentations, getDatasetDetailPartialInfo } from 'api/dataset'
import { getDetailRole } from 'containers/Settings/AccessRole/selectors'
import { Info } from 'containers/Nodes/screens/components/Info'
import { VuiThumbnailItems } from '@topkamera/viso-ui'
import './style.scss'

const Augmentation = (props) => {
  const { color = LIBRARY_ACCENT, workspace, detailRole, router } = props
  const [augmentationsData, setAugmentationsData] = useState([])
  const [previewImage, setPreviewImage] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [useDefaultSettings, toggleUseDefaultSettings] = useState(true)
  const [augmentationTypes, setAugmentationTypes] = useState([])
  const [augmentationStep, setAugmentationStep] = useState(1)
  const [datasetInfo, setDatasetInfo] = useState({})
  const [selectedEditor, setSelectedEditor] = useState(augmentationTypes[0])
  const [blurTypes, setBlurTypes] = useState([])
  const [noiseTypes, setNoiseTypes] = useState([])
  const [cropTypes, setCropTypes] = useState([])
  const { id } = useParams()
  const permission = getPermission('permissionDataset', detailRole.role)

  useEffect(() => {
    if (permission.includes('edit') && workspace.augmentationEnabled) {
      async function fetchAugmentations() {
        setIsLoading(true)

        const datasetInfo = await getDatasetDetailPartialInfo({ id })
        if (datasetInfo.data.errors) {
          defaultNotifier(datasetInfo.data.errors)
          return
        }

        setDatasetInfo(datasetInfo.data.data.dataset)

        const { data } = await augmentations()
        setIsLoading(false)
        if (data.errors) {
          defaultNotifier(data.errors)
          return
        }

        const getResponseData = data.data.augmentations || []
        const getBlurType = getResponseData.find(item => item.key === 'augment_blur') || {}
        const mapBlurTypes = getBlurType.options?.blur_type?.map(type => ({ blur_type: type, kernel_size: getBlurType.defaultValues.kernel_size })) || []
        const getNoiseType = getResponseData.find(item => item.key === 'augment_noise') || {}
        const mapNoiseTypes = getNoiseType.options?.noise_type?.map(type => ({ noise_type: type, noise_level: getNoiseType.defaultValues.noise_level })) || []
        const getCropType = getResponseData.find(item => item.key === 'augment_crop') || {}
        const mapCropTypes = getCropType.options?.crop_type?.map(type => ({ crop_type: type, crop_min_ratio: getCropType.defaultValues.crop_min_ratio, crop_max_ratio: getCropType.defaultValues.crop_max_ratio })) || []
        const findPreviewImage = getResponseData.find(item => item.key === 'augment_flip')?.url
        setPreviewImage(findPreviewImage)
        setAugmentationsData(getResponseData.map(item => ({ ...item, label: item.name })))
        setBlurTypes(mapBlurTypes)
        setNoiseTypes(mapNoiseTypes)
        setCropTypes(mapCropTypes)
      }

      fetchAugmentations()
    } else {
      handleCloseModal()
    }
  }, [])

  const submitClass = className('button', {
    [`button_${color}`]: color,
  })

  const onAugmentationTypeSelect = (item) => {
    console.log({ augmentationsData })
    const exists = augmentationTypes.find(i => i.key === item.key)
    const updateTypes = exists ? augmentationTypes.filter(i => i.key !== item.key) : [...augmentationTypes, item]
    const updateImages = augmentationsData.map(image => ({ ...image, isSelected: updateTypes.some(t => t.key === image.key) }))
    console.log({ updateImages })
    setAugmentationsData(updateImages)
    setAugmentationTypes(updateTypes)
    setSelectedEditor(updateTypes[0])
  }

  const onContinueSelectAugmentation = () => {
    const setStep = useDefaultSettings ? 3 : 2
    setAugmentationStep(setStep)
  }

  const handleCloseModal = () => {
    const wsPath = workspace && slugify(workspace.name)
    router.navigate(`/${wsPath}/dataset/${id}`)
  }

  const handleConfirmModal = () => {
    const wsPath = workspace && slugify(workspace.name)
    router.navigate(`/${wsPath}/dataset`)
  }


  if (augmentationStep === 3) {
    return (
      <AugmentationConfirmation
        dataset={datasetInfo}
        color={color}
        selectedAugmentations={augmentationTypes}
        setSelectedEditor={setSelectedEditor}
        onClose={handleCloseModal}
        onConfirm={handleConfirmModal}
        onAugmentationClick={onAugmentationTypeSelect}
        changeStep={setAugmentationStep}
        blurTypes={blurTypes}
        noiseTypes={noiseTypes}
        cropTypes={cropTypes}
        useDefaultSettings={useDefaultSettings}
        toggleUseDefaultSettings={toggleUseDefaultSettings}
      />
    )
  }

  if (augmentationStep === 2) {
    return (
      <AugmentationEditor
        color={color}
        selectedAugmentations={augmentationTypes}
        previewImage={previewImage}
        onClose={handleCloseModal}
        onSubmit={() => setAugmentationStep(3)}
        submitClass={submitClass}
        selectedEditor={selectedEditor}
        setSelectedEditor={setSelectedEditor}
        setSelectedAugmentations={setAugmentationTypes}
        blurTypes={blurTypes}
        setBlurTypes={setBlurTypes}
        noiseTypes={noiseTypes}
        setNoiseTypes={setNoiseTypes}
        cropTypes={cropTypes}
        setCropTypes={setCropTypes}
      />
    )
  }

  return (
    <ProcessPage onCancel={handleCloseModal} customClass='augmentation-popup'>
      {() => (
        <div className='popup__cont dataset-slider'>
          <div className='login'>
            <div className='headline'>
              <h1 className='headline__title'>Choose Augmentations</h1>
              <div className='headline__subtitle'>
                Expand the dataset <span className={color}>{datasetInfo && datasetInfo?.name}</span> for better model training.
              </div>
            </div>

            {isLoading ?
              <LoadingIndicator
                isProcessPage
                color={color}
                scrollClass='augmentation-loading'
                title='Loading Assistant'
                description={<span>Please wait while we are getting things ready.<br /> It'll just take a moment.</span>}
              /> :
              <>
                <Modal
                  classNames='modal__frame7'
                  title={'Select Augmentations'}
                  titleClass='modal__title_label'
                  body={(
                    <>
                      <>
                        <div className='modal_body__description'>
                          Choose transformations that suit your dataset to generate diverse samples and help your model perform better in real-world conditions.
                        </div>
                        <div className='modal_body__augmentation'>
                          <div className='modal_body__title'>
                            Augmentation Types *
                          </div>
                          <div className='augmentation-popup__list vui-pb-8'>
                            <VuiThumbnailItems
                              items={augmentationsData}
                              showLoader
                              color={color}
                              onClick={(item) => onAugmentationTypeSelect(item)}
                              radius='md'
                            />
                          </div>
                          <Info textColor='label'>
                            Existing annotations will be auto augmented and applied to the augmented images where applicable.
                          </Info>
                        </div>

                        <Switch
                          color={color}
                          name='updateCreditCard'
                          value={useDefaultSettings}
                          text='Apply default settings'
                          onChange={() => toggleUseDefaultSettings(!useDefaultSettings)}
                        />
                      </>
                    </>
                  )}
                  onClose={handleCloseModal}
                />

                <div className='login__actions'>
                  <Button
                    dataCy='submit-model'
                    disabled={augmentationTypes.length === 0}
                    onClick={onContinueSelectAugmentation}
                    className={submitClass}
                  >
                    <FormattedMessage {...messages.continue} />
                  </Button>
                </div>
              </>
            }

          </div>
        </div>
      )}
    </ProcessPage>
  )
}

const mapStateToProps = createStructuredSelector({
  workspace: makeSelectFetchWorkspace(),
  detailRole: getDetailRole()
})

export default connect(mapStateToProps, {})(
  compose(withRouter)(Augmentation)
)